import { useState } from 'react';

import { ActionIcon, Group, Paper, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconMinus, IconPlus } from '@tabler/icons-react';

import { useIsMobile } from '@/hooks/useIsMobile';
import { useChartStore } from '@/store/chartStore';
import type { ChartData } from '@/types/chart';

interface LegendProps {
  chartData: ChartData;
}

export default function Legend({ chartData }: LegendProps) {
  const colorScheme = useChartStore((state) => state.config.colorScheme);
  const hiddenCategories = useChartStore((state) => state.hiddenCategories);
  const toggleCategory = useChartStore((state) => state.toggleCategory);
  const isMobile = useIsMobile();
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Only show legend for categorical charts
  if (!chartData.categoryMapping) {
    return null;
  }

  const { categories, direction = 'ascending' } = chartData.categoryMapping;

  const legendItems = categories.map((category, index) => {
    // Calculate normalized value for this category respecting direction
    const effectiveIndex = direction === 'descending' ? categories.length - 1 - index : index;
    const normalizedValue = (effectiveIndex + 1) / categories.length;
    const color = colorScheme.getColor(
      { hand: '', row: 0, col: 0, type: 'pair', subtype: 'premium-pair', segments: [] },
      normalizedValue,
      category,
    );
    const isHidden = hiddenCategories.has(category);

    return (
      <button
        key={category}
        type="button"
        onClick={() => toggleCategory(category)}
        aria-label={`${isHidden ? 'Show' : 'Hide'} ${category} category`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          cursor: 'pointer',
          opacity: isHidden ? 0.4 : 1,
          transition: 'opacity 0.2s',
          background: 'none',
          border: 'none',
          padding: 0,
          font: 'inherit',
          color: 'inherit',
          width: '100%',
        }}
        onMouseEnter={(e) => {
          if (!isHidden) e.currentTarget.style.opacity = '0.7';
        }}
        onMouseLeave={(e) => {
          if (!isHidden) e.currentTarget.style.opacity = '1';
        }}
      >
        <div
          style={{
            width: 20,
            height: 20,
            backgroundColor: color,
            borderRadius: 4,
            flexShrink: 0,
            border: isHidden ? '2px solid var(--mantine-color-dark-4)' : 'none',
            boxSizing: 'border-box',
          }}
        />
        <Text size="sm" style={{ textDecoration: isHidden ? 'line-through' : 'none' }}>
          {category}
        </Text>
      </button>
    );
  });

  return (
    <Paper shadow="md" p="sm" radius="md">
      <Stack gap="xs">
        <Group justify="space-between" gap="xs">
          <Stack gap={0}>
            <Text size="sm" fw={600}>
              Legend
            </Text>
            {!isCollapsed && (
              <Text size="xs" c="dimmed" style={{ fontStyle: 'italic' }}>
                Click to show/hide categories
              </Text>
            )}
          </Stack>
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-label={isCollapsed ? 'Expand legend' : 'Collapse legend'}
          >
            {isCollapsed ? <IconPlus size={16} /> : <IconMinus size={16} />}
          </ActionIcon>
        </Group>
        {!isCollapsed &&
          (isMobile ? (
            <SimpleGrid cols={2} spacing="xs">
              {legendItems}
            </SimpleGrid>
          ) : (
            legendItems
          ))}
      </Stack>
    </Paper>
  );
}
